import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../home/Icon';
import api from '../wrapper/API';
import CustomDropdown from '../input/CustomDropdown';
import StatsCard from './StatsCard';
import { readThemeColors } from './chartColors';
import { ChartTooltip } from './ChartTooltip';
import { buildAttendanceSegments, formatAttendanceValue } from './attendanceSegments';
import { addOnIconColor, addOnIncomeBars } from './addOnStats';
import IncomeBreakdownChart from './IncomeBreakdownChart';
import { useDarkMode } from '../context/DarkModeContext';
import { eventLabelOf } from '../../util/eventRules';
import {
  ResponsiveContainer,
  PieChart, Pie, Cell,
  Tooltip, Legend, Label
} from 'recharts';

const fmtEuro = (cents) => ((cents ?? 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (isoDate) => {
  const d = new Date(isoDate);
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}.${d.getFullYear()}`;
};

function DonutLabel({ viewBox, total, sublabel, colors }) {
  const { cx, cy } = viewBox;
  return (
    <text textAnchor="middle">
      <tspan x={cx} y={cy - 8} fontSize="22" fontWeight="700" fill={colors.text} fontFamily="var(--font-body)">{total}</tspan>
      <tspan x={cx} y={cy + 14} fontSize="11" fill={colors.textSecondary} fontFamily="var(--font-body)">{sublabel}</tspan>
    </text>
  );
}

function EventView({ preselectedEventId }) {
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useDarkMode();
  // readThemeColors() reads CSS variables at runtime; it has no arg but depends on the active theme.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = useMemo(() => readThemeColors(), [isDarkMode]);

  useEffect(() => {
    api.get('/Statistics/GetPastEvents')
      .then(res => {
        setEvents(res.data);
        if (preselectedEventId && res.data.some(e => e.id === preselectedEventId)) {
          setSelectedId(preselectedEventId);
        } else if (res.data.length > 0) {
          setSelectedId(res.data[0].id);
        }
      })
      .catch(() => {});
  }, [preselectedEventId]);

  useEffect(() => {
    if (selectedId == null) {
      // If preselected (via query param) event is in future → still try to fetch its stats directly.
      if (preselectedEventId) {
        setLoading(true);
        api.get(`/Statistics/GetEventStats/${preselectedEventId}`)
          .then(res => setStats(res.data))
          .catch(() => setStats(null))
          .finally(() => setLoading(false));
      }
      return;
    }
    setLoading(true);
    api.get(`/Statistics/GetEventStats/${selectedId}`)
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [selectedId, preselectedEventId]);

  if (events.length === 0 && !preselectedEventId) {
    return <p className="statsEmpty">Noch keine Events verfügbar.</p>;
  }

  const income = stats?.income ?? {};
  const presence = stats?.presence ?? {};
  const breakdown = stats?.breakdown ?? {};
  const capacity = stats?.capacity ?? {};

  const utilizationPct = capacity.maxPlayers > 0
    ? Math.round((capacity.confirmed / capacity.maxPlayers) * 100)
    : 0;

  const presenceData = stats ? [
    { name: 'Anwesend',  value: presence.total,   color: colors.positive },
    { name: 'Abwesend',  value: presence.absent,  color: colors.negative },
    { name: 'Offen',     value: presence.pending, color: colors.warning }
  ].filter(d => d.value > 0) : [];

  const attendanceData = stats ? buildAttendanceSegments(breakdown, stats.participantGroups, colors) : [];

  // "Eintritt" + one bar per add-on (sign-up share of the paid prices + on-site sales together, by add-on name;
  // the tooltip shows the split). Old events: rental as "Leihausrüstung", articles under their names.
  const incomeSplit = stats ? [
    { name: 'Eintritt', Betrag: (income.entryCents ?? 0) / 100 },
    ...addOnIncomeBars(income.addOns),
  ].filter(d => d.Betrag > 0) : [];
  // Usage per add-on of the process (+ add-ons that only have lines): "Leihausrüstung 5 / 8".
  const addOnUsage = Array.isArray(capacity.addOns) ? capacity.addOns : [];

  // Einnahmen = bezahlte Beträge (auch Vorkasse ohne Erscheinen) + Artikel. In der Vorschau zusätzlich
  // die noch erwarteten Zahlungen (openCents: schuldet, unbezahlt, nicht abwesend).
  const openCents = income.openCents ?? 0;
  const incomeSubLines = stats ? [
    stats.isPreview ? 'Bezahlt + erwartet + Artikel' : 'Bezahlte Beträge + Artikel',
    openCents > 0
      ? (stats.isPreview ? `davon offen: ${fmtEuro(openCents)} €` : `noch offen: ${fmtEuro(openCents)} € (nicht enthalten)`)
      : null,
  ] : [];

  // Flat rates of the event's process (none for cancelled events or events without process): one line per
  // flat rate; "Name: 2 × 10,00 €" when it is due several times (role flat rates per present leader).
  // Deductions ("Abzüge") have the same shape and are listed the same way.
  const costSubLines = (items) => (items ?? []).map(f => (f.count > 1
    ? `${f.name}: ${f.count} × ${fmtEuro(f.amountCents)} €`
    : `${f.name}: ${fmtEuro(f.totalCents)} €`));
  const flatRateSubLines = costSubLines(income.flatRates);
  const deductionSubLines = costSubLines(income.deductions);
  // Subtotal before the flat rates: income minus leader salary.
  const afterSalaryCents = (income.totalCents ?? 0) - (income.salaryOrgaCents ?? 0);

  const presenceTotal = presenceData.reduce((s, d) => s + (d.value || 0), 0);
  const attendanceTotal = attendanceData.reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div className="statsEventView">
      <div className="statsFilterRow">
        <label className="statsFilterLabel">Spieltag:</label>
        <CustomDropdown
          icon="event"
          value={String(selectedId ?? '')}
          onChange={(e) => setSelectedId(Number(e.target.value))}
        >
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>
              {formatDate(ev.date)} — {eventLabelOf(ev)}{ev.locationName ? ` · ${ev.locationName}` : ''} ({ev.confirmedCount} Teiln.)
            </option>
          ))}
        </CustomDropdown>
      </div>

      {loading && <p className="statsEmpty">Lade Event-Statistiken...</p>}

      {stats && !loading && (
        <>
          {stats.isPreview && (
            <div className="statsPreviewBanner">
              <Icon icon="warning" className="statsPreviewIcon" />
              <div>
                <strong>Vorschau</strong>
                <span>Dieser Spieltag ist noch nicht abgeschlossen. Spieler ohne Anwesenheitsstatus werden als anwesend gewertet; die Einnahmen enthalten bereits bezahlte Beträge plus noch erwartete Zahlungen — die Zahlen können sich noch ändern.</span>
              </div>
            </div>
          )}
          <div className="statsCardGrid">
            <StatsCard title="Einnahmen"        value={fmtEuro(income.totalCents)}        suffix="€" icon="payments"  tone="positive" subLines={incomeSubLines} />
            <StatsCard title="Lohn Spieltagleiter" value={fmtEuro(income.salaryOrgaCents)} suffix="€" icon="sports"   tone="negative" sub={`${breakdown.orga ?? 0} Spieltagleiter anwesend`} />
            <StatsCard title="Einnahmen abzüglich Lohn" value={fmtEuro(afterSalaryCents)} suffix="€" icon="account_balance_wallet" tone={afterSalaryCents >= 0 ? 'positive' : 'negative'} sub="Einnahmen − Lohn Spieltagleiter" />
            <StatsCard title="Pauschalen"       value={fmtEuro(income.flatRateCents)}     suffix="€" icon="request_quote" tone="negative" subLines={flatRateSubLines} sub="Keine Pauschalen" />
            <StatsCard title="Abzüge"           value={fmtEuro(income.deductionCents ?? 0)} suffix="€" icon="remove_circle" tone="negative" subLines={deductionSubLines} sub="Keine Abzüge" />
            <StatsCard title="Netto (Gewinn)"   value={fmtEuro(income.profitCents)}       suffix="€" icon="savings"       tone={(income.profitCents ?? 0) >= 0 ? 'positive' : 'negative'} />
            <StatsCard title="Auslastung"       value={`${capacity.confirmed} / ${capacity.maxPlayers}`} icon="group" tone="neutral" sub={`${utilizationPct}% Auslastung`} />
            <StatsCard title="No-Shows"         value={presence.absent} icon="person_off"   tone={presence.absent > 0 ? 'warning' : 'neutral'} />
            <StatsCard title="Warteliste"       value={capacity.waitlist} icon="schedule"   tone="neutral" />
            {/* One "Nutzungen" card per add-on: "/ max" only with a limit; 0 = unbegrenzt; no process row
                (maxQuantity null) = only the count. The icon takes the add-on color (none = default). */}
            {addOnUsage.map(a => (
              <StatsCard
                key={`addon-${a.name}`}
                title={a.name}
                value={a.maxQuantity > 0 ? `${a.used ?? 0} / ${a.maxQuantity}` : `${a.used ?? 0}`}
                icon="inventory_2"
                iconColor={addOnIconColor(a.colorKey)}
                tone="neutral"
                sub={a.maxQuantity === 0 ? 'Nutzungen · unbegrenzt' : 'Nutzungen'}
              />
            ))}
          </div>

          <div className="statsChartGrid">
            <div className="statsChartCard">
              <h3>Anwesenheit</h3>
              {presenceData.length === 0 ? (
                <p className="statsEmpty">Keine Daten.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={presenceData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={82}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {presenceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      <Label
                        content={({ viewBox }) => (
                          <DonutLabel viewBox={viewBox} total={presenceTotal} sublabel="Spieler" colors={colors} />
                        )}
                      />
                    </Pie>
                    <Tooltip
                      isAnimationActive={false}
                      content={({ active, payload }) => (
                        <ChartTooltip active={active} payload={payload} />
                      )}
                    />
                    <Legend wrapperStyle={{ color: colors.text, fontSize: 13 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="statsChartCard">
              <h3>Gruppen &amp; Rollen</h3>
              {attendanceData.length === 0 ? (
                <p className="statsEmpty">Keine Teilnehmer.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={attendanceData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={52}
                      outerRadius={82}
                      paddingAngle={2}
                      strokeWidth={0}
                    >
                      {attendanceData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                      <Label
                        content={({ viewBox }) => (
                          <DonutLabel viewBox={viewBox} total={attendanceTotal} sublabel="Gesamt" colors={colors} />
                        )}
                      />
                    </Pie>
                    <Tooltip
                      isAnimationActive={false}
                      content={({ active, payload }) => (
                        <ChartTooltip active={active} payload={payload} formatValue={formatAttendanceValue} />
                      )}
                    />
                    <Legend wrapperStyle={{ color: colors.text, fontSize: 13 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="statsChartCard wide">
              <h3>Einnahmen-Breakdown</h3>
              {incomeSplit.length === 0 ? (
                <p className="statsEmpty">Keine Einnahmen.</p>
              ) : (
                <IncomeBreakdownChart data={incomeSplit} colors={colors} gradientId="evGradBreak" />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EventView;
